import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useListboxCombobox } from '../../lib/hooks/useListboxCombobox';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Lock,
  Pencil,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { LoadingIndicator, LoadingSpinner } from '../../components/onboarding/LoadingIndicator';
import OnboardingStepper, {
  OnboardingStep,
} from '../../components/onboarding/OnboardingStepper';
import OnboardingSubHeader from '../../components/onboarding/OnboardingSubHeader';
import {
  OnboardingSurface,
  OnboardingNestedPanel,
} from '../../components/onboarding/Surface';
import { useAuth } from '../../contexts/AuthContext';
import {
  changeDatabaseType,
  DEFAULT_LIST_PAGE_SIZE,
  getProjectById,
  listProjectUsers as fetchListProjectUsers,
  listUsers,
  updateProject as patchProject,
} from '../../services/onboardingApi';
import { userFromBackend } from '../../services/onboardingTransforms';
import { useDebouncedValue } from '../../lib/hooks/useDebouncedValue';
import { usePaginatedList } from '../../lib/hooks/usePaginatedList';
import { useScrollPaginatedLoadMore } from '../../lib/hooks/useScrollPaginatedLoadMore';
import axiosInterceptor from '../../utils/axios/axiosInterceptor';
import {
  canAssignUsers,
  DATABASE_TYPES,
  DEFAULT_POSTGRES_SCHEMA,
  formatPlatformRoleLabel,
  isSupportedDatabaseType,
  formatUserDisplayName,
  getUserInitials,
  isPlatformAdminUser,
  MAX_ORG_PROJECT_NAME_LENGTH,
  ORG_PROJECT_NAME_LENGTH_HINT,
  isOrgOrProjectNameTooLong,
  orgOrProjectNameLengthMessage,
  orgRoleForUserInOrganization,
  PostgresSettings,
  User,
} from '../../types/onboarding';
import { PostgresSettingsFields } from '../../components/onboarding/PostgresSettingsFields';
import { Button } from '../../components/onboarding/Button';
import { LockedField } from '../../components/onboarding/LockedField';
import { Input } from '../../components/onboarding/Input';
import { Label } from '../../components/onboarding/Label';
import { Dialog } from '../../components/onboarding/Dialog';
import { InfrastructureStatusNotice } from '../../components/onboarding/InfrastructureStatusNotice';
import { twMerge as cn } from 'tailwind-merge';
import { pageRoutes } from '../../constants/routes';
import { PlatformAdminOrgNotice, ActiveOrganizationNotice } from '../../components/onboarding/ActiveOrganizationNotice';

/** Map PROJECT_CONFIG / project_configuration `system` to onboarding `databaseType` value. */
function resolveDatabaseTypeFromConfig(
  system: string | null | undefined
): string {
  if (system == null || String(system).trim() === '') return '';
  const raw = String(system).trim();
  const lower = raw.toLowerCase();
  for (const db of DATABASE_TYPES) {
    if (db.value === lower || db.label.toLowerCase() === lower) {
      return db.value;
    }
  }
  if (lower.includes('mysql')) return 'mysql';
  if (lower.includes('mssql') || lower.includes('sql server')) return 'mssql';
  if (lower.includes('postgres')) return 'postgres';
  return '';
}

function databaseTypeDisplayLabel(databaseType: string): string {
  const found = DATABASE_TYPES.find((db) => db.value === databaseType);
  if (found) return found.label;
  if (databaseType.trim()) return databaseType.trim();
  return 'N/A';
}

/** Engine label including the PostgreSQL version, e.g. "PostgreSQL 16". */
function engineDisplayLabel(databaseType: string, postgresVersion: string): string {
  const label = databaseTypeDisplayLabel(databaseType);
  return databaseType === 'postgres' && postgresVersion ? `${label} ${postgresVersion}` : label;
}

interface EngineSelection {
  databaseType: string;
  postgresVersion: string;
}

/** True when saving would re-provision: the engine changes, or only the PostgreSQL version. */
function engineChanged(before: EngineSelection, after: EngineSelection): boolean {
  if (before.databaseType !== after.databaseType) return true;
  return after.databaseType === 'postgres' && before.postgresVersion !== after.postgresVersion;
}

// PostgreSQL truncates identifiers longer than 63 characters.
const MAX_POSTGRES_IDENTIFIER_LENGTH = 63;

function postgresFieldErrors(formData: ProjectFormData): Partial<ProjectFormErrors> {
  if (formData.databaseType !== 'postgres') return {};
  const errors: Partial<ProjectFormErrors> = {};
  if (!formData.postgresVersion) {
    errors.postgresVersion = 'PostgreSQL version is required';
  }
  const schema = formData.schemaName.trim();
  if (schema.length > MAX_POSTGRES_IDENTIFIER_LENGTH) {
    errors.schemaName = `Schema must be at most ${MAX_POSTGRES_IDENTIFIER_LENGTH} characters`;
  } else if (schema.includes('"')) {
    errors.schemaName = 'Schema must not contain double quotes';
  }
  return errors;
}

/** PostgreSQL settings to send for a postgres project; undefined for other engines. */
function postgresSettingsFor(formData: ProjectFormData): PostgresSettings | undefined {
  if (formData.databaseType !== 'postgres') return undefined;
  return {
    engineMajorVersion: Number(formData.postgresVersion),
    schemaName: formData.schemaName.trim() || DEFAULT_POSTGRES_SCHEMA,
  };
}

interface ProjectFormData {
  name: string;
  organization: string;
  databaseType: string;
  /** PostgreSQL major version as a string; '' until chosen. Only used for postgres. */
  postgresVersion: string;
  /** Only used for postgres; empty means the default schema. */
  schemaName: string;
  startDate: string;
  endDate: string;
  assignedUsers: string[];
}

type ProjectFormErrors = {
  name?: string;
  organization?: string;
  databaseType?: string;
  postgresVersion?: string;
  schemaName?: string;
  startDate?: string;
  endDate?: string;
};

function ProjectOnboarding() {
  const navigate = useNavigate();
  const { projectId: projectIdParam } = useParams<{ projectId?: string }>();
  const editProjectId =
    projectIdParam && /^\d+$/.test(projectIdParam)
      ? Number(projectIdParam)
      : undefined;
  const isEditMode = Boolean(editProjectId && editProjectId > 0);

  const steps = useMemo<OnboardingStep[]>(
    () => [
      {
        id: 1,
        title: 'Project Details',
        description: isEditMode
          ? 'Edit the project name or end date. Other fields are locked after creation.'
          : 'Enter basic project information and customer details.',
      },
      {
        id: 2,
        title: 'Assign Users',
        description: isEditMode
          ? 'Add or remove team members on this project.'
          : 'Select team members to work on this project.',
      },
      {
        id: 3,
        title: isEditMode ? 'Review & Save' : 'Review & Create',
        description: isEditMode
          ? 'Confirm changes to dates and team membership.'
          : 'Review project details and confirm creation.',
      },
    ],
    [isEditMode]
  );

  const {
    currentUser,
    organizations,
    ensureOrganizations,
    addProject,
    addOrganizationName,
    isLoading,
    refresh,
  } = useAuth();
  const [userEmailsById, setUserEmailsById] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedUserSearch = useDebouncedValue(searchQuery, 300);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    organization: '',
    databaseType: '',
    postgresVersion: '',
    schemaName: DEFAULT_POSTGRES_SCHEMA,
    startDate: '',
    endDate: '',
    assignedUsers: [],
  });
  const [errors, setErrors] = useState<ProjectFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clearFieldErrors = useCallback((...fields: (keyof ProjectFormErrors)[]) => {
    setErrors((prev) => {
      const toClear = fields.filter((field) => prev[field]);
      if (toClear.length === 0) return prev;
      const next = { ...prev };
      for (const field of toClear) {
        delete next[field];
      }
      return next;
    });
    setSubmitError(null);
  }, []);
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [editLoadError, setEditLoadError] = useState<
    'not_found' | 'error' | null
  >(null);
  const initialEditSnapshotRef = useRef<{
    name: string;
    endDate: string;
    databaseType: string;
    postgresVersion: string;
    assignedIds: Set<string>;
  } | null>(null);
  const loadedEditProjectIdRef = useRef<number | null>(null);
  /** Raw project_users ids from API before org-role reconciliation. */
  const pendingRawAssignmentIdsRef = useRef<string[] | null>(null);
  /** Maintainers/admins with stale project_users rows — removed on save. */
  const staleNonDeveloperAssignmentIdsRef = useRef<Set<string>>(new Set());
  const assignmentReconciledForProjectRef = useRef<number | null>(null);
  const [dbTypeConfirmOpen, setDbTypeConfirmOpen] = useState(false);
  const [infraProvisionStatus, setInfraProvisionStatus] = useState<
    'pending' | 'in_progress' | null
  >(null);

  const customerSearchInputRef = useRef<HTMLInputElement>(null);
  const customerPortalMenuRef = useRef<HTMLDivElement>(null);
  const orgUsersScrollRef = useRef<HTMLDivElement>(null);
  const orgUsersSentinelRef = useRef<HTMLDivElement>(null);
  const newOrgInputRef = useRef<HTMLInputElement>(null);
  const [customerMenuRect, setCustomerMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const closeAddOrgModal = useCallback(() => {
    setShowAddOrgModal(false);
    setNewOrgName('');
  }, []);

  const filteredOrganizations = useMemo(() => {
    const pool = isPlatformAdminUser(currentUser)
      ? organizations
      : organizations.filter(
          (org) =>
            currentUser?.organization &&
            org.name.toLowerCase() ===
              currentUser.organization.toLowerCase()
        );
    return pool.filter((org) =>
      org.name.toLowerCase().includes(orgSearchQuery.toLowerCase())
    );
  }, [organizations, orgSearchQuery, currentUser]);

  useEffect(() => {
    void ensureOrganizations();
  }, [ensureOrganizations]);

  const selectedOrg = useMemo(
    () =>
      organizations.find(
        (org) =>
          org.name.trim().toLowerCase() ===
          formData.organization.trim().toLowerCase()
      ),
    [organizations, formData.organization]
  );

  const fetchOrgUsersPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      search,
      signal,
    }: {
      pageNumber: number;
      pageSize: number;
      search?: string;
      signal?: AbortSignal;
    }) => {
      if (!formData.organization.trim()) {
        return {
          items: [],
          page_number: 0,
          page_size: pageSize,
          total_count: 0,
          has_more: false,
        };
      }
      const orgId = selectedOrg ? Number(selectedOrg.id) : undefined;
      const response = await listUsers({
        pageNumber,
        pageSize,
        search,
        signal,
        organizationId:
          orgId != null && !Number.isNaN(orgId) && orgId > 0
            ? orgId
            : undefined,
      });
      return {
        items: (response.users ?? []).map(userFromBackend),
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    [formData.organization, selectedOrg]
  );

  const {
    items: orgUsers,
    isLoading: orgUsersLoading,
    isLoadingMore: orgUsersLoadingMore,
    hasMore: orgUsersHasMore,
    loadMore: loadMoreOrgUsers,
  } = usePaginatedList<User>({
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    fetchPage: fetchOrgUsersPage,
    search: debouncedUserSearch,
    enabled:
      Boolean(formData.organization.trim()) &&
      (currentStep >= 2 || isEditMode),
    filters: { organization: formData.organization },
    getItemKey: (user) => user.id,
  });

  const selectCustomerOrg = useCallback(
    (index: number) => {
      const org = filteredOrganizations[index];
      if (!org) return;
      clearFieldErrors('organization');
      setFormData((prev) => ({ ...prev, organization: org.name }));
      setOrgSearchQuery('');
    },
    [filteredOrganizations, clearFieldErrors],
  );

  const customerMenu = useListboxCombobox({
    itemCount: filteredOrganizations.length,
    onSelect: selectCustomerOrg,
    additionalRefs: [customerPortalMenuRef],
  });

  const measureCustomerMenuRect = useCallback(() => {
    const el = customerMenu.containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const reservedBottom = 16;
    const spaceBelow = window.innerHeight - rect.bottom - gap - reservedBottom;
    const maxHeight = Math.max(160, Math.min(320, spaceBelow));
    setCustomerMenuRect({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, [customerMenu.containerRef]);

  useLayoutEffect(() => {
    if (!customerMenu.isOpen) {
      setCustomerMenuRect(null);
      return;
    }
    measureCustomerMenuRect();
    window.addEventListener('resize', measureCustomerMenuRect);
    window.addEventListener('scroll', measureCustomerMenuRect, true);
    return () => {
      window.removeEventListener('resize', measureCustomerMenuRect);
      window.removeEventListener('scroll', measureCustomerMenuRect, true);
    };
  }, [customerMenu.isOpen, measureCustomerMenuRect]);

  // When the dropdown opens, move focus to the search input so typing and
  // arrow-key navigation "just work" without a second click.
  useEffect(() => {
    if (customerMenu.isOpen) {
      customerSearchInputRef.current?.focus();
    }
  }, [customerMenu.isOpen]);

  useEffect(() => {
    if (isEditMode || isPlatformAdminUser(currentUser) || !currentUser?.organization) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      organization: currentUser.organization,
    }));
  }, [isEditMode, currentUser]);

  useEffect(() => {
    clearFieldErrors('organization');
  }, [formData.organization, clearFieldErrors]);

  // Clear stale user picks when the admin walks back and changes organization.
  useEffect(() => {
    if (isEditMode) return;
    setFormData((prev) => ({ ...prev, assignedUsers: [] }));
  }, [formData.organization, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !editProjectId || !currentUser) {
      if (!isEditMode) {
        setEditLoading(false);
        initialEditSnapshotRef.current = null;
        pendingRawAssignmentIdsRef.current = null;
        staleNonDeveloperAssignmentIdsRef.current = new Set();
        assignmentReconciledForProjectRef.current = null;
        loadedEditProjectIdRef.current = null;
      }
      return;
    }

    if (loadedEditProjectIdRef.current === editProjectId) {
      return;
    }

    let cancelled = false;
    setEditLoading(true);
    setEditLoadError(null);
    (async () => {
      try {
        const p = await getProjectById(editProjectId);
        if (!p) {
          if (!cancelled) {
            setEditLoadError('not_found');
            setEditLoading(false);
          }
          return;
        }

        const pu = await fetchListProjectUsers(editProjectId);
        if (cancelled) return;

        const assignedIds = (pu.users ?? []).map((u) => String(u.user_id));
        pendingRawAssignmentIdsRef.current = assignedIds;
        staleNonDeveloperAssignmentIdsRef.current = new Set();
        assignmentReconciledForProjectRef.current = null;
        setUserEmailsById(
          Object.fromEntries(
            (pu.users ?? []).map((u) => [String(u.user_id), u.email_id])
          )
        );
        const endSlice = p.end_date?.slice(0, 10) ?? '';

        let databaseType = '';
        let postgresVersion = '';
        let schemaName = DEFAULT_POSTGRES_SCHEMA;
        try {
          const api = axiosInterceptor(true);
          const cfg = await api.get<{
            project_config?: {
              system?: string;
              engine_major_version?: number;
              schema_name?: string;
            };
          }>(`/project/${editProjectId}/project-configuration`);
          const projectConfig = cfg.data?.project_config;
          databaseType = resolveDatabaseTypeFromConfig(projectConfig?.system);
          if (projectConfig?.engine_major_version != null) {
            postgresVersion = String(projectConfig.engine_major_version);
          }
          schemaName = projectConfig?.schema_name || DEFAULT_POSTGRES_SCHEMA;
        } catch {
          /* project may have no configuration row yet */
        }

        setFormData({
          name: p.project_name,
          organization: p.organization_name,
          databaseType,
          postgresVersion,
          schemaName,
          startDate: p.start_date?.slice(0, 10) ?? '',
          endDate: endSlice,
          assignedUsers: assignedIds,
        });
        const ps = (p.provision_status ?? '').toLowerCase();
        if (ps === 'pending' || ps === 'in_progress') {
          setInfraProvisionStatus(ps as 'pending' | 'in_progress');
        } else {
          setInfraProvisionStatus(null);
        }
        initialEditSnapshotRef.current = {
          name: p.project_name,
          endDate: endSlice,
          databaseType,
          postgresVersion,
          assignedIds: new Set(assignedIds),
        };
        loadedEditProjectIdRef.current = editProjectId;
      } catch {
        if (!cancelled) setEditLoadError('error');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, editProjectId, currentUser]);

  const canAssign = Boolean(
    currentUser && canAssignUsers(currentUser.platformRole)
  );
  const isPlatformAdmin = isPlatformAdminUser(currentUser);

  // Project membership is developer-only; maintainers already have org-wide access.
  const assignableUsers = useMemo(() => {
    if (!formData.organization) return [];

    return orgUsers.filter((u) => {
      if (isPlatformAdminUser(u)) return false;
      return orgRoleForUserInOrganization(u, formData.organization) === 'developer';
    });
  }, [orgUsers, formData.organization]);

  const filteredUsers = assignableUsers;

  useEffect(() => {
    if (!isEditMode || !editProjectId || !formData.organization.trim()) return;
    if (assignmentReconciledForProjectRef.current === editProjectId) return;

    const raw = pendingRawAssignmentIdsRef.current;
    if (!raw) return;
    if (orgUsersLoading) return;

    const org = formData.organization;
    const developerIds: string[] = [];
    const nonDeveloperIds: string[] = [];
    let unresolved = 0;

    for (const id of raw) {
      const user = orgUsers.find((u) => u.id === id);
      if (!user) {
        if (!orgUsersHasMore) {
          developerIds.push(id);
        } else {
          unresolved += 1;
        }
        continue;
      }
      const role = orgRoleForUserInOrganization(user, org);
      if (role === 'developer') {
        developerIds.push(id);
      } else if (role === 'maintainer' || role === 'admin') {
        nonDeveloperIds.push(id);
      } else {
        developerIds.push(id);
      }
    }

    if (unresolved > 0) return;

    assignmentReconciledForProjectRef.current = editProjectId;
    pendingRawAssignmentIdsRef.current = null;
    staleNonDeveloperAssignmentIdsRef.current = new Set(nonDeveloperIds);

    setFormData((prev) => ({ ...prev, assignedUsers: developerIds }));

    if (initialEditSnapshotRef.current) {
      initialEditSnapshotRef.current = {
        ...initialEditSnapshotRef.current,
        assignedIds: new Set(developerIds),
      };
    }
  }, [
    isEditMode,
    editProjectId,
    formData.organization,
    orgUsers,
    orgUsersLoading,
    orgUsersHasMore,
  ]);

  useScrollPaginatedLoadMore({
    scrollContainerRef: orgUsersScrollRef,
    sentinelRef: orgUsersSentinelRef,
    hasMore: orgUsersHasMore,
    isLoading: orgUsersLoading,
    isLoadingMore: orgUsersLoadingMore,
    onLoadMore: loadMoreOrgUsers,
    enabled:
      Boolean(formData.organization.trim()) &&
      (currentStep >= 2 || isEditMode),
    itemCount: filteredUsers.length,
  });

  if ((isLoading && !currentUser) || (isEditMode && editLoading)) {
    return (
      <>
        <OnboardingSubHeader
          title={isEditMode ? 'Edit project' : 'Project Onboarding'}
          showBackButton
          backRoute={
            isEditMode ? pageRoutes.onboardingProjects : pageRoutes.onboarding
          }
        />
        <main className="flex flex-1 flex-col bg-surface-muted">
          <LoadingIndicator layout="page" />
        </main>
      </>
    );
  }

  if (isEditMode && editLoadError) {
    return (
      <>
        <OnboardingSubHeader
          title="Edit project"
          showBackButton
          backRoute={pageRoutes.onboardingProjects}
        />
        <main className="bg-surface-muted px-8 py-16">
          <OnboardingSurface className="mx-auto max-w-3xl p-8 text-center">
            <p className="text-gray-700">
              {editLoadError === 'not_found'
                ? 'This project was not found or you do not have access.'
                : 'Could not load project details. Please try again.'}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-6"
              onClick={() => navigate(pageRoutes.onboardingProjects)}
            >
              Back to projects
            </Button>
          </OnboardingSurface>
        </main>
      </>
    );
  }

  const validateStep1 = (): boolean => {
    if (isEditMode) {
      const newErrors: ProjectFormErrors = { ...postgresFieldErrors(formData) };
      if (!formData.name.trim()) {
        newErrors.name = 'Project name is required';
      } else if (isOrgOrProjectNameTooLong(formData.name)) {
        newErrors.name = orgOrProjectNameLengthMessage('Project name');
      }
      if (formData.endDate && formData.startDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (end <= start) {
          newErrors.endDate = 'End date must be after start date';
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    const newErrors: ProjectFormErrors = { ...postgresFieldErrors(formData) };

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (isOrgOrProjectNameTooLong(formData.name)) {
      newErrors.name = orgOrProjectNameLengthMessage('Project name');
    }

    if (!formData.organization) {
      newErrors.organization = 'Organization is required';
    } else if (isOrgOrProjectNameTooLong(formData.organization)) {
      newErrors.organization = orgOrProjectNameLengthMessage('Organization name');
    }
    if (!formData.databaseType) {
      newErrors.databaseType = 'Database type is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (formData.endDate && formData.startDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 2 is intentionally not gated on assigned-user count: a brand-new
  // organisation will not have any users yet, and admins should still be able
  // to create the project shell first and onboard users afterwards via the
  // backend's update_project add_user flow. See README onboarding doc for the
  // org/user/project bootstrap sequence.

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    setSubmitError(null);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (submitting) return;

    // If the database type has changed in edit mode, show the destructive confirm
    // modal first. The actual submission runs once the user confirms.
    if (isEditMode) {
      const snap = initialEditSnapshotRef.current;
      if (snap && engineChanged(snap, formData) && !infraProvisionStatus) {
        setDbTypeConfirmOpen(true);
        return;
      }
    }

    await _doSubmit();
  };

  const _doSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isEditMode) {
        const snap = initialEditSnapshotRef.current;
        if (!snap || !editProjectId) {
          setSubmitError('Missing project state. Please reload the page.');
          return;
        }
        const pid = editProjectId;
        const curName = formData.name.trim();
        if (curName && curName !== snap.name.trim()) {
          await patchProject(pid, { project_name: curName });
        }
        const curEnd = formData.endDate.trim();
        const snapEnd = snap.endDate.trim();
        if (curEnd !== snapEnd && curEnd) {
          await patchProject(pid, { end_date: curEnd });
        }
        if (
          isSupportedDatabaseType(formData.databaseType) &&
          engineChanged(snap, formData)
        ) {
          await changeDatabaseType(
            pid,
            formData.databaseType,
            postgresSettingsFor(formData)
          );
        }
        const currentSet = new Set(formData.assignedUsers);
        for (const idStr of Array.from(staleNonDeveloperAssignmentIdsRef.current)) {
          await patchProject(pid, { remove_user_id: Number(idStr) });
        }
        for (const idStr of Array.from(snap.assignedIds)) {
          if (!currentSet.has(idStr)) {
            await patchProject(pid, { remove_user_id: Number(idStr) });
          }
        }
        for (const idStr of formData.assignedUsers) {
          if (!snap.assignedIds.has(idStr)) {
            const email =
              userEmailsById[idStr] ??
              orgUsers.find((x) => x.id === idStr)?.email;
            if (!email) continue;
            await patchProject(pid, {
              add_user_email: email.trim().toLowerCase(),
            });
          }
        }
        await refresh();
        navigate(
          `${pageRoutes.onboardingProjects}?success=project-updated`
        );
        return;
      }

      await addProject({
        name: formData.name,
        customer: formData.organization,
        databaseType: formData.databaseType,
        postgres: postgresSettingsFor(formData),
        assignedUsers: formData.assignedUsers,
        assignedUserEmails: formData.assignedUsers.map(
          (id) =>
            userEmailsById[id] ??
            orgUsers.find((user) => user.id === id)?.email ??
            ''
        ),
        startDate: new Date(formData.startDate),
        endDate: formData.endDate ? new Date(formData.endDate) : undefined,
      });
      navigate(`${pageRoutes.onboarding}?success=project`);
    } catch (err: any) {
      setSubmitError(
        err?.displayMessage ??
          (isEditMode
            ? 'Failed to save project changes. Please try again.'
            : 'Failed to create project. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserAssignment = (userId: string) => {
    const user = orgUsers.find((entry) => entry.id === userId);
    if (user) {
      setUserEmailsById((prev) => ({ ...prev, [userId]: user.email }));
    }
    setFormData((prev) => ({
      ...prev,
      assignedUsers: prev.assignedUsers.includes(userId)
        ? prev.assignedUsers.filter((id) => id !== userId)
        : [...prev.assignedUsers, userId],
    }));
  };

  return (
    <>
      <OnboardingSubHeader
        title={isEditMode ? 'Edit project' : 'Project Onboarding'}
        showBackButton
        backRoute={
          isEditMode ? pageRoutes.onboardingProjects : pageRoutes.onboarding
        }
      />
      <OnboardingStepper steps={steps} currentStep={currentStep} />

      <main className="bg-surface-muted px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {currentStep === 1 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 1: Project Details"
              >
                Step 1: Project Details
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {isEditMode
                  ? 'Update the project name, end date, or database type on this step. Other fields were set when the project was created.'
                  : 'Enter the basic information about the new project.'}
              </p>
              {isEditMode && infraProvisionStatus && (
                <InfrastructureStatusNotice
                  status={infraProvisionStatus}
                  className="mb-6"
                />
              )}
              {!isEditMode && isPlatformAdmin && formData.organization ? (
                <PlatformAdminOrgNotice
                  organizationName={formData.organization}
                  entityLabel="project"
                  className="mb-8"
                />
              ) : !isEditMode && !isPlatformAdmin ? (
                <ActiveOrganizationNotice className="mb-8" />
              ) : (
                <div className="mb-8" />
              )}

              {isEditMode && (
                <div className="mb-8 space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="projectName-edit">
                      Project Name <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-gray-500">
                      Renaming only changes the display name — AWS resource names are unaffected.{' '}
                      {ORG_PROJECT_NAME_LENGTH_HINT}.
                    </p>
                    <Input
                      id="projectName-edit"
                      placeholder="Enter project name"
                      maxLength={MAX_ORG_PROJECT_NAME_LENGTH}
                      value={formData.name}
                      onChange={(e) => {
                        clearFieldErrors('name');
                        setFormData({ ...formData, name: e.target.value });
                      }}
                      className={cn(errors.name && 'border-red-500')}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>
                  <LockedField
                    label="Organization"
                    value={formData.organization || 'N/A'}
                  />
                  <div className="space-y-2">
                    <Label>Database type</Label>
                    <p className="text-xs text-gray-500">
                      Changing the type, or the PostgreSQL version, will delete all
                      runs, queries, and cloud infrastructure, then re-provision
                      with the new engine.
                    </p>
                    {infraProvisionStatus && (
                      <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                        <LoadingSpinner size="sm" className="text-yellow-600" />
                        <span>
                          Infrastructure provisioning is{' '}
                          <strong>
                            {infraProvisionStatus === 'in_progress'
                              ? 'in progress'
                              : 'queued'}
                          </strong>
                          . The database type cannot be changed until it
                          completes.
                        </span>
                      </div>
                    )}
                    <div
                      className={cn(
                        'flex gap-6 p-3 border border-gray-200 rounded-md',
                        infraProvisionStatus && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {DATABASE_TYPES.map((db) => (
                        <label
                          key={db.value}
                          className={cn(
                            'flex items-center gap-2',
                            infraProvisionStatus
                              ? 'cursor-not-allowed'
                              : 'cursor-pointer'
                          )}
                        >
                          <input
                            type="radio"
                            name="databaseType-edit"
                            value={db.value}
                            checked={formData.databaseType === db.value}
                            disabled={!!infraProvisionStatus}
                            onChange={(e) =>
                              setFormData({ ...formData, databaseType: e.target.value })
                            }
                            className="w-4 h-4 text-accent border-gray-300 focus:ring-accent disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-700">{db.label}</span>
                        </label>
                      ))}
                    </div>
                    {formData.databaseType === 'postgres' && (
                      <PostgresSettingsFields
                        version={formData.postgresVersion}
                        schemaName={formData.schemaName}
                        onVersionChange={(postgresVersion) => {
                          clearFieldErrors('postgresVersion');
                          setFormData({ ...formData, postgresVersion });
                        }}
                        onSchemaNameChange={(schemaName) => {
                          clearFieldErrors('schemaName');
                          setFormData({ ...formData, schemaName });
                        }}
                        versionError={errors.postgresVersion}
                        schemaNameError={errors.schemaName}
                        disabled={!!infraProvisionStatus}
                        schemaDisabled={initialEditSnapshotRef.current?.databaseType === 'postgres'}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        <Lock className="w-3 h-3 shrink-0" aria-hidden />
                        Start Date
                      </Label>
                      <p className="min-h-8 text-xs text-gray-500">
                        Not editable after creation
                      </p>
                      <Input
                        value={
                          formData.startDate
                            ? new Date(
                                `${formData.startDate}T12:00:00`
                              ).toLocaleDateString()
                            : 'N/A'
                        }
                        disabled
                        readOnly
                        tabIndex={-1}
                        className="bg-gray-100 text-gray-700 cursor-not-allowed"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="endDate-edit-wizard"
                        className="flex items-center gap-1.5"
                      >
                        <span className="w-3 shrink-0" aria-hidden />
                        End Date
                      </Label>
                      <p className="min-h-8 text-xs text-gray-500">
                        Must be after the project start date.
                      </p>
                      <Input
                        id="endDate-edit-wizard"
                        type="date"
                        value={formData.endDate}
                        min={formData.startDate || undefined}
                        onChange={(e) => {
                          clearFieldErrors('endDate');
                          setFormData({ ...formData, endDate: e.target.value });
                        }}
                        className={cn(
                          'block onboarding-date-input',
                          errors.endDate && 'border-red-500',
                        )}
                      />
                      {errors.endDate && (
                        <p className="text-sm text-red-500">{errors.endDate}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isEditMode && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="projectName">
                    Project Name <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Unique project identifier (not editable after creation).{' '}
                    {ORG_PROJECT_NAME_LENGTH_HINT}.
                  </p>
                  <Input
                    id="projectName"
                    placeholder="Enter project name"
                    maxLength={MAX_ORG_PROJECT_NAME_LENGTH}
                    value={formData.name}
                    onChange={(e) => {
                      clearFieldErrors('name');
                      setFormData({ ...formData, name: e.target.value });
                    }}
                    className={cn(errors.name && 'border-red-500')}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  {isPlatformAdmin ? (
                    <>
                  <Label>
                    Organization Name <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Client company name (not editable after creation).{' '}
                    {ORG_PROJECT_NAME_LENGTH_HINT}.
                  </p>
                  <div className="relative" ref={customerMenu.containerRef}>
                    <button
                      type="button"
                      onClick={customerMenu.toggle}
                      onKeyDown={customerMenu.onKeyDown}
                      aria-haspopup="listbox"
                      aria-expanded={customerMenu.isOpen}
                      aria-controls={customerMenu.menuId}
                      className={cn(
                        'w-full px-3 py-2 border rounded-md text-left flex items-center justify-between gap-2 bg-white',
                        errors.organization
                          ? 'border-red-500'
                          : 'border-gray-200',
                        'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent'
                      )}
                    >
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate',
                          formData.organization
                            ? 'text-gray-900'
                            : 'text-gray-500'
                        )}
                        title={formData.organization || undefined}
                      >
                        {formData.organization ||
                          'Search and select organization'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 text-gray-500 transition-transform',
                          customerMenu.isOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    {customerMenu.isOpen &&
                      customerMenuRect &&
                      createPortal(
                        <div
                          ref={customerPortalMenuRef}
                          className="border border-gray-200 bg-white shadow-lg"
                          style={{
                            position: 'fixed',
                            top: customerMenuRect.top,
                            left: customerMenuRect.left,
                            width: customerMenuRect.width,
                            maxHeight: customerMenuRect.maxHeight,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            zIndex: 2147483646,
                            borderRadius: 6,
                            boxShadow:
                              '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                          }}
                        >
                          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                ref={customerSearchInputRef}
                                type="text"
                                placeholder="Search organizations..."
                                value={orgSearchQuery}
                                onChange={(e) =>
                                  setOrgSearchQuery(e.target.value)
                                }
                                onKeyDown={customerMenu.onKeyDown}
                                role="combobox"
                                aria-expanded={customerMenu.isOpen}
                                aria-controls={customerMenu.menuId}
                                aria-autocomplete="list"
                                aria-activedescendant={
                                  customerMenu.activeOptionId
                                }
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div id={customerMenu.menuId} role="listbox">
                            {filteredOrganizations.length > 0 ? (
                              filteredOrganizations.map((org, index) => (
                                <button
                                  key={org.id}
                                  type="button"
                                  role="option"
                                  id={customerMenu.getOptionId(index)}
                                  aria-selected={
                                    formData.organization === org.name
                                  }
                                  onClick={() => customerMenu.select(index)}
                                  onMouseEnter={() =>
                                    customerMenu.setActiveIndex(index)
                                  }
                                  className={cn(
                                    'w-full px-3 py-2 text-left text-sm flex items-center justify-between',
                                    formData.organization === org.name &&
                                      'bg-accent/5',
                                    customerMenu.activeIndex === index
                                      ? 'bg-gray-100'
                                      : 'hover:bg-gray-50'
                                  )}
                                >
                                  <span className="min-w-0 flex-1 break-words text-left">
                                    {org.name}
                                  </span>
                                  {formData.organization === org.name && (
                                    <Check className="w-4 h-4 text-accent" />
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No organizations found
                              </div>
                            )}
                          </div>
                          <div className="border-t border-gray-100 sticky bottom-0 bg-white">
                            <button
                              type="button"
                              onClick={() => {
                                customerMenu.close();
                                setShowAddOrgModal(true);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm flex items-center gap-2 text-accent"
                            >
                              <Plus className="w-4 h-4" />
                              Add New Organization
                            </button>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>
                    </>
                  ) : (
                    <LockedField
                      label="Organization"
                      required
                      value={
                        formData.organization || currentUser?.organization || 'N/A'
                      }
                    />
                  )}
                  {errors.organization && (
                    <p className="text-sm text-red-500">{errors.organization}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>
                    Database Type <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Client&apos;s source DB engine (not editable after creation)
                  </p>
                  <div
                    className={cn(
                      'flex gap-6 p-3 border rounded-md',
                      errors.databaseType
                        ? 'border-red-500'
                        : 'border-gray-200'
                    )}
                  >
                    {DATABASE_TYPES.map((db) => (
                      <label
                        key={db.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="databaseType"
                          value={db.value}
                          checked={formData.databaseType === db.value}
                          onChange={(e) => {
                            clearFieldErrors('databaseType');
                            setFormData({
                              ...formData,
                              databaseType: e.target.value,
                            });
                          }}
                          className="w-4 h-4 text-accent border-gray-300 focus:ring-accent"
                        />
                        <span className="text-sm text-gray-700">
                          {db.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.databaseType && (
                    <p className="text-sm text-red-500">{errors.databaseType}</p>
                  )}
                  {formData.databaseType === 'postgres' && (
                    <PostgresSettingsFields
                      version={formData.postgresVersion}
                      schemaName={formData.schemaName}
                      onVersionChange={(postgresVersion) => {
                        clearFieldErrors('postgresVersion');
                        setFormData({ ...formData, postgresVersion });
                      }}
                      onSchemaNameChange={(schemaName) => {
                        clearFieldErrors('schemaName');
                        setFormData({ ...formData, schemaName });
                      }}
                      versionError={errors.postgresVersion}
                      schemaNameError={errors.schemaName}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">
                      Start Date <span className="text-red-500">*</span>
                    </Label>
                    <p className="min-h-8 text-xs text-gray-500">
                      Not editable after creation
                    </p>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => {
                        clearFieldErrors('startDate', 'endDate');
                        setFormData({ ...formData, startDate: e.target.value });
                      }}
                      className={cn(
                        'block onboarding-date-input',
                        errors.startDate && 'border-red-500',
                      )}
                    />
                    {errors.startDate && (
                      <p className="text-sm text-red-500">{errors.startDate}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date</Label>
                    <p className="min-h-8 text-xs text-gray-500">
                      Editable by Admin or Maintainer
                    </p>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate || undefined}
                      onChange={(e) => {
                        clearFieldErrors('endDate');
                        setFormData({ ...formData, endDate: e.target.value });
                      }}
                      className={cn(
                        'block onboarding-date-input',
                        errors.endDate && 'border-red-500',
                      )}
                    />
                    {errors.endDate && (
                      <p className="text-sm text-red-500">{errors.endDate}</p>
                    )}
                  </div>
                </div>
              </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(
                      isEditMode
                        ? pageRoutes.onboardingProjects
                        : pageRoutes.onboarding,
                    )
                  }
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={isEditMode && !!infraProvisionStatus}
                  title={
                    isEditMode && infraProvisionStatus
                      ? 'Infrastructure is being provisioned — editing is locked until it completes'
                      : undefined
                  }
                >
                  Continue
                </Button>
              </div>
            </OnboardingSurface>
          )}

          {currentStep === 2 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 2: Assign Users"
              >
                Step 2: Assign Users
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                {isEditMode
                  ? 'Toggle developers to add or remove them from this project. Maintainers already have access to all organization projects and are not listed here. Changes are applied when you finish the last step.'
                  : 'Select developers in this organization who will work on this project. Maintainers already have access to all projects in their organization.'}
              </p>

              {!canAssign && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <p className="text-sm text-yellow-800">
                      You do not have permission to assign users. Contact an
                      Admin or Maintainer.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {!isEditMode && formData.assignedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedUsers.map((userId) => {
                      const user =
                        orgUsers.find((u) => u.id === userId) ??
                        (userEmailsById[userId]
                          ? {
                              id: userId,
                              email: userEmailsById[userId],
                              firstName: '',
                              lastName: '',
                            }
                          : undefined);
                      if (!user) return null;
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm"
                        >
                          {formatUserDisplayName(user.firstName, user.lastName)}
                          <button
                            type="button"
                            onClick={() => toggleUserAssignment(userId)}
                            className="hover:bg-accent/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                <OnboardingNestedPanel
                  ref={orgUsersScrollRef}
                  className="max-h-80 divide-y divide-gray-200 overflow-y-auto"
                >
                  {orgUsersLoading && filteredUsers.length === 0 ? (
                    <div className="flex justify-center px-4 py-8">
                      <LoadingSpinner size="lg" className="text-brand" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      {assignableUsers.length === 0 ? (
                        <>
                          No users in{' '}
                          <span className="font-medium text-gray-700">
                            {formData.organization || 'this organization'}
                          </span>{' '}
                          {isEditMode ? (
                            <>yet. Add users to this organization first.</>
                          ) : (
                            <>
                              yet. You can create the project and onboard users
                              afterwards via User Onboarding.
                            </>
                          )}
                        </>
                      ) : (
                        <>No users match &ldquo;{searchQuery}&rdquo;.</>
                      )}
                    </div>
                  ) : (
                    filteredUsers.map((user) => {
                      const isSelected = formData.assignedUsers.includes(
                        user.id
                      );
                      const roleInOrg =
                        orgRoleForUserInOrganization(
                          user,
                          formData.organization
                        ) ?? 'developer';
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => toggleUserAssignment(user.id)}
                          className={cn(
                            'w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors',
                            isSelected && 'bg-accent/5'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {getUserInitials(user.firstName, user.lastName)}
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-gray-900">
                                {formatUserDisplayName(user.firstName, user.lastName)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {user.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span
                              className={cn(
                                'px-2 py-1 rounded text-xs font-medium',
                                roleInOrg === 'admin'
                                  ? 'bg-red-100 text-red-700'
                                  : roleInOrg === 'maintainer'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              )}
                            >
                              {formatPlatformRoleLabel(roleInOrg)}
                            </span>
                            <div
                              className={cn(
                                'w-5 h-5 rounded border-2 flex items-center justify-center',
                                isSelected
                                  ? 'bg-accent border-accent'
                                  : 'border-gray-300'
                              )}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                  <div
                    ref={orgUsersSentinelRef}
                    className="flex justify-center px-4 py-3"
                  >
                    {orgUsersLoadingMore && (
                      <LoadingSpinner size="sm" className="text-brand" />
                    )}
                  </div>
                </OnboardingNestedPanel>
              </div>

              {formData.assignedUsers.length === 0 && (
                <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  {isEditMode
                    ? 'No users on this project yet. Select users from the list above to assign them.'
                    : 'No users assigned. You can add users to this project after it is created.'}
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {currentStep === 3 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label={
                  isEditMode ? 'Step 3: Review and Save' : 'Step 3: Review and Create'
                }
              >
                {isEditMode
                  ? 'Step 3: Review & Save'
                  : 'Step 3: Review & Create'}
              </h2>
              <p className="text-gray-600 text-sm mb-2">
                {isEditMode
                  ? 'Review your changes before saving.'
                  : 'Review the project details before creating.'}
              </p>
              <p className="text-xs text-gray-500 mb-8">
                {isEditMode
                  ? 'Locked fields were set when the project was created. Name, end date, database type, and team changes are applied when you save.'
                  : 'Fields marked with a lock icon cannot be changed after creation.'}
              </p>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <LockedField
                    label="Organization"
                    value={formData.organization}
                  />
                  {isEditMode &&
                  initialEditSnapshotRef.current?.name.trim() !==
                    formData.name.trim() ? (
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-accent shrink-0" />
                        Project Name
                        <span className="text-xs text-accent">(changing)</span>
                      </p>
                      <p className="text-gray-900 mt-1 break-all">
                        <span className="line-through text-gray-400 mr-2">
                          {initialEditSnapshotRef.current?.name}
                        </span>
                        → {formData.name}
                      </p>
                    </div>
                  ) : isEditMode ? (
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-accent shrink-0" />
                        Project Name
                        <span className="text-xs text-accent">(editable)</span>
                      </p>
                      <p className="text-gray-900 mt-1 break-all">{formData.name}</p>
                    </div>
                  ) : (
                    <LockedField
                      label="Project Name"
                      value={formData.name}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {isEditMode &&
                  initialEditSnapshotRef.current &&
                  engineChanged(initialEditSnapshotRef.current, formData) ? (
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-red-500 shrink-0" />
                        Database Type
                        <span className="text-xs text-red-500">(changing — data will be reset)</span>
                      </p>
                      <p className="text-gray-900 mt-1">
                        <span className="line-through text-gray-400 mr-2">
                          {engineDisplayLabel(
                            initialEditSnapshotRef.current.databaseType,
                            initialEditSnapshotRef.current.postgresVersion
                          )}
                        </span>
                        → {engineDisplayLabel(formData.databaseType, formData.postgresVersion)}
                      </p>
                    </div>
                  ) : isEditMode ? (
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-accent shrink-0" />
                        Database Type
                        <span className="text-xs text-accent">(editable)</span>
                      </p>
                      <p className="text-gray-900 mt-1">
                        {engineDisplayLabel(formData.databaseType, formData.postgresVersion)}
                      </p>
                    </div>
                  ) : (
                    <LockedField
                      label="Database Type"
                      value={engineDisplayLabel(formData.databaseType, formData.postgresVersion)}
                    />
                  )}
                  <LockedField
                    label="Start Date"
                    value={
                      formData.startDate
                        ? new Date(
                            `${formData.startDate}T12:00:00`
                          ).toLocaleDateString()
                        : 'N/A'
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                      <Pencil className="w-3 h-3 text-accent shrink-0" />
                      End Date
                      <span className="text-xs text-accent">(editable)</span>
                    </p>
                    <p className="text-gray-900 mt-1 break-all">
                      {formData.endDate
                        ? new Date(
                            `${formData.endDate}T12:00:00`
                          ).toLocaleDateString()
                        : 'Not specified'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                    <Pencil className="w-3 h-3 text-accent" />
                    Assigned Users ({formData.assignedUsers.length})
                    <span className="text-xs text-accent">(editable)</span>
                  </p>
                  {formData.assignedUsers.length === 0 ? (
                    <p className="text-sm italic text-gray-500">
                      {isEditMode
                        ? 'None on this project. Add users on step 2.'
                        : 'None assigned yet. You can add users after the project is created.'}
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {formData.assignedUsers.map((userId) => {
                        const user =
                        orgUsers.find((u) => u.id === userId) ??
                        (userEmailsById[userId]
                          ? {
                              id: userId,
                              email: userEmailsById[userId],
                              firstName: '',
                              lastName: '',
                            }
                          : undefined);
                        if (!user) return null;
                        return (
                          <span
                            key={userId}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm max-w-full"
                          >
                            <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0">
                              {getUserInitials(user.firstName, user.lastName)}
                            </div>
                            <span className="break-all min-w-0">
                              {formatUserDisplayName(user.firstName, user.lastName)}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {submitError && (
                <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-accent hover:bg-accent-hover text-white"
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      {isEditMode ? 'Saving…' : 'Creating...'}
                    </span>
                  ) : isEditMode ? (
                    'Save changes'
                  ) : (
                    'Create Project'
                  )}
                </Button>
              </div>
            </OnboardingSurface>
          )}
        </div>
      </main>

      <Dialog
        open={dbTypeConfirmOpen}
        onClose={() => { if (!submitting) setDbTypeConfirmOpen(false); }}
        labelledBy="db-type-change-title"
        panelClassName="max-w-md"
      >
        <h2 id="db-type-change-title" className="text-lg font-bold text-brand">
          Change database type?
        </h2>
        <p className="mt-3 text-sm text-gray-600">
          You are changing the database type from{' '}
          <span className="font-semibold">
            {engineDisplayLabel(
              initialEditSnapshotRef.current?.databaseType ?? '',
              initialEditSnapshotRef.current?.postgresVersion ?? ''
            )}
          </span>{' '}
          to{' '}
          <span className="font-semibold">
            {engineDisplayLabel(formData.databaseType, formData.postgresVersion)}
          </span>
          .
        </p>
        <p className="mt-2 text-sm text-gray-600">
          All runs, queries, and cloud infrastructure (S3 bucket, RDS
          databases, Secrets Manager secrets) will be permanently deleted and
          re-provisioned with the new engine. This cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDbTypeConfirmOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            disabled={submitting}
            onClick={async () => {
              setDbTypeConfirmOpen(false);
              await _doSubmit();
            }}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Saving…
              </span>
            ) : (
              'Confirm change'
            )}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={showAddOrgModal}
        onClose={closeAddOrgModal}
        labelledBy="project-add-org-title"
        describedBy="project-add-org-description"
        initialFocusRef={newOrgInputRef}
      >
        <h3
          id="project-add-org-title"
          className="text-lg font-bold text-brand mb-4"
        >
          Add New Organization
        </h3>
        <p
          id="project-add-org-description"
          className="text-sm text-gray-600 mb-4"
        >
          Enter the name of the new client organization. {ORG_PROJECT_NAME_LENGTH_HINT}.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newOrgName">
              Organization Name <span className="text-red-500">*</span>
            </Label>
            <Input
              ref={newOrgInputRef}
              id="newOrgName"
              placeholder="Enter organization name"
              maxLength={MAX_ORG_PROJECT_NAME_LENGTH}
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={closeAddOrgModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const trimmed = newOrgName.trim();
                if (!trimmed) return;
                clearFieldErrors('organization');
                addOrganizationName(trimmed);
                setFormData({
                  ...formData,
                  organization: trimmed,
                });
                closeAddOrgModal();
              }}
              disabled={!newOrgName.trim()}
              className="bg-accent hover:bg-accent-hover text-white"
            >
              Add Organization
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default ProjectOnboarding;
